import React, { useId, useState, forwardRef } from "react";
import { Input } from "./input";
import { Eye, EyeOff } from "lucide-react";

/**
 * Accessible show/hide password field. Renders a masked <input type="password">
 * with an eye/eye-off toggle inside the field. Preserves autocomplete and
 * password-manager behavior by keeping the input's `type` on the native input
 * and `name`/`autoComplete` attributes on the same element that is submitted.
 *
 * The visible value is never logged or persisted — visibility is transient UI
 * state (React `useState`) that resets on unmount.
 */
type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  /** data-testid for the input element itself. The toggle button testid is
   *  derived by appending `-toggle`. */
  "data-testid"?: string;
  /** Optional test id for the toggle button (overrides the derived one). */
  toggleTestId?: string;
};

export const PasswordInput = forwardRef<HTMLInputElement, Props>(function PasswordInput(
  { "data-testid": testId, toggleTestId, className, ...rest }, ref,
) {
  const [visible, setVisible] = useState(false);
  const fallbackId = useId();
  const inputId = rest.id ?? fallbackId;

  return (
    <div className="relative">
      <Input
        {...rest}
        ref={ref}
        id={inputId}
        type={visible ? "text" : "password"}
        className={`pr-10 ${className ?? ""}`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-controls={inputId}
        aria-pressed={visible}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
        tabIndex={0}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-r"
        data-testid={toggleTestId ?? (testId ? `${testId}-toggle` : undefined)}
      >
        {visible ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
      </button>
    </div>
  );
});
